import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Race, RaceAsset } from '../../contexts/BettingProvider';
import { useGameSession } from '../../contexts/GameSessionProvider';
import { useWallet } from '../../contexts/WalletProvider';

import { useRoundTimers } from '../../hooks/useRoundTimers';
import { useBettingState } from '../../hooks/useBettingState';
import { useShareBoost } from '../../hooks/useShareBoost';
import { useSolanaWallets } from '@privy-io/react-auth/solana';
import RaceTrack from './RaceTrack';
import ProgressionLadder from './ProgressionLadder';
import GameSessionControls from './GameSessionControls';
import BetNotificationContainer from './BetNotificationContainer';
import ResultsTicker from './ResultsTicker';
import { ShareModal } from '../Modal';
import { BetNotification } from './BetNotification';
import { calculateSortedAssetsWithPerformance } from '../../utils/raceCalculations';
import { getAssetLogo, getAssetCSSColor } from '../../utils/assetHelpers';
import './BettingContainer.css';
import './ProgressionLadder.css';



const BettingContainer: React.FC = () => {
  const gameSession = useGameSession();
  const { address, isAuthenticated } = useWallet();
  const { wallets } = useSolanaWallets();
  
  // ============================================================================
  // DATA ADAPTERS - Convert GameSession matchup to Race format
  // ============================================================================
  
  const currentRace: Race | null = useMemo(() => {
    if (!gameSession.raceMatchup) return null;
    
    const matchup = gameSession.raceMatchup;
    
    // Convert matchup history to raceWinners format (for placement dots)
    const raceWinners: Array<{ 
      first: { symbol: string; percentage: number }; 
      second: { symbol: string; percentage: number } | null;
      third: { symbol: string; percentage: number } | null;
    }> = [];
    
    if (gameSession.matchupHistory && Array.isArray(gameSession.matchupHistory)) {
      gameSession.matchupHistory.forEach((raceResult: any) => {
        const winnerSymbol = raceResult.winner;
        const winnerPercentage = raceResult[winnerSymbol]?.gain || 0;
        
        raceWinners.push({
          first: { symbol: winnerSymbol, percentage: winnerPercentage },
          second: null,
          third: null
        });
      });
    }
    
    const championAsset: RaceAsset = {
      symbol: matchup.champion,
      name: matchup.champion,
      logo: getAssetLogo(matchup.champion),
      recentPerformance: [0, 0, 0, 0, matchup.championPercentGain],
      avgPerformance: matchup.championPercentGain,
      odds: 1.0,
      currentPrice: matchup.championPrice,
      raceWinners
    };
    
    const challengerAsset: RaceAsset = {
      symbol: matchup.challenger,
      name: matchup.challenger,
      logo: getAssetLogo(matchup.challenger),
      recentPerformance: [0, 0, 0, 0, matchup.challengerPercentGain],
      avgPerformance: matchup.challengerPercentGain,
      odds: 1.0,
      currentPrice: matchup.challengerPrice,
      raceWinners
    };
    
    return {
      id: gameSession.currentRound?.round_id || 'demo-race',
      name: `${matchup.champion} vs ${matchup.challenger}`,
      assets: [championAsset, challengerAsset],
      duration: 15,
      status: matchup.isLive ? 'live' : 'upcoming'
    };
  }, [gameSession.raceMatchup, gameSession.currentRound, gameSession.matchupHistory]);
  
  const currentPrices = useMemo(() => {
    const adapted: { [symbol: string]: { price: number; confidence: number; timestamp: number } } = {};
    Object.entries(gameSession.allPrices).forEach(([symbol, data]) => {
      adapted[symbol] = {
        price: data.price,
        confidence: 1,
        timestamp: data.timestamp
      };
    });
    return adapted;
  }, [gameSession.allPrices]);
  
  const currentRound = useMemo(() => {
    if (!gameSession.raceMatchup) return null;
    
    const matchup = gameSession.raceMatchup;
    const round = gameSession.currentRound;
    
    // Demo mode: no phase (cars render in idle state)
    // Active session: use backend phase (BETTING/RACING/SETTLEMENT/RESULTS)
    const phase = gameSession.currentSession && round ? round.phase : undefined;
    
    return {
      roundId: round?.round_id || 'demo-round',
      phase: phase,
      startingPrices: matchup.startingPrices ? {
        [matchup.champion]: matchup.startingPrices.champion,
        [matchup.challenger]: matchup.startingPrices.challenger
      } : {
        [matchup.champion]: matchup.championPrice,
        [matchup.challenger]: matchup.challengerPrice
      },
      endingPrices: null,
      signedRaceDetails: null,
      player_won: round?.player_won,
      race_winner: round?.race_winner,
      player_decision: round?.player_decision,
      player_pick: round?.player_pick,
      timestamps: round ? {
        bettingEnd: round.betting_end_time,
        bettingStart: round.betting_start_time,
        raceEnd: round.race_end_time,
        raceStart: round.race_start_time
      } : null
    };
  }, [gameSession.raceMatchup, gameSession.currentRound, gameSession.currentSession]);
  
  const isLoading = gameSession.isLoading;
  const error = gameSession.error;
  const clearError = gameSession.clearError;
  
  // ============================================================================
  // HOOKS & STATE
  // ============================================================================
  
  const solanaWallet = useMemo(() => {
    if (!wallets || wallets.length === 0) return null;
    return wallets[0];
  }, [wallets]);
  
  const { 
    bettingTimeRemaining, 
    racingTimeRemaining,
    isBettingPhase,
    isBettingEnabled 
  } = useRoundTimers(currentRound, 'connected');
  
  const {
    betAmount
  } = useBettingState(currentRace ? [currentRace] : [], currentRound, isBettingPhase);
  
  const [showRaceStartModal, setShowRaceStartModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [notifications, setNotifications] = useState<BetNotification[]>([]);
  
  // Share boost hook for Twitter share modal
  const { hasClaimedBoost, hasUnusedBoost, sessionIdUsed, shownThisSession, boostValue, refreshStatus } = useShareBoost();
  
  // Determine if boost applies to current session
  // Boost applies if: unused OR (claimed AND used in THIS session)
  const currentSessionId = gameSession.currentSession?.session_id;
  const boostAppliesToCurrentSession = hasUnusedBoost || (hasClaimedBoost && sessionIdUsed === currentSessionId);
  
  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================
  
  const addNotification = (notification: Omit<BetNotification, 'id'>) => {
    const newNotification: BetNotification = {
      ...notification,
      id: `${Date.now()}-${Math.random()}`
    };
    setNotifications(prev => [...prev, newNotification]);
  };
  
  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================
  
  // Show race start modal on phase transition
  const prevPhaseRef = useRef<string | null>(null);
  
  useEffect(() => {
    const currentPhase = currentRound?.phase;
    const previousPhase = prevPhaseRef.current;
    

    
    if (previousPhase === 'BETTING' && currentPhase === 'RACING') {
      setShowRaceStartModal(true);
      setTimeout(() => {
        setShowRaceStartModal(false);
      }, 1500); // ✅ Shorter duration - WebP loads instantly
    }
    
    prevPhaseRef.current = currentPhase || null;
  }, [currentRound?.phase]);
  
  // Show share modal when user wins Round 1 (and hasn't claimed boost yet)
  useEffect(() => {
    const isRound1Win = 
      gameSession.currentRound?.phase === 'RESULTS' && 
      gameSession.currentRound?.player_won === true &&
      gameSession.currentSession?.rounds_completed === 1;
    
    // Show modal if: won round 1, hasn't claimed boost, and haven't shown this session
    if (isRound1Win && !hasClaimedBoost && !shownThisSession) {
      // Small delay so the win result displays first
      const timer = setTimeout(() => {
        setShowShareModal(true);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [
    gameSession.currentRound?.phase, 
    gameSession.currentRound?.player_won, 
    gameSession.currentSession?.rounds_completed, 
    hasClaimedBoost, 
    shownThisSession
  ]);
  
  // ✅ No video loading logic needed - WebP displays instantly like an image!
  
  // ============================================================================
  // RENDER
  // ============================================================================
  
  // Note: Errors are now displayed inline in GameSessionControls (small, dismissible)
  // No full-screen error takeover - keeps UI functional even with errors
  
  // Safety check: If no race data, don't render (should be caught by App.tsx loading check)
  if (!currentRace) {
    return null;
  }

  // Recalculate on every render - prices change in real-time during race!
  const sortedAssetsWithPerformance = calculateSortedAssetsWithPerformance(currentRace, currentRound, currentPrices);
  
  // Calculate win/loss status for ProgressionLadder
  const isRacing = currentRound?.phase === 'RACING' || currentRound?.phase === 'SETTLEMENT' || currentRound?.phase === 'RESULTS';
  let winLossStatus: 'winning' | 'losing' | 'won' | 'lost' | 'cashed_out' | null = null;
  
  if (isRacing && currentRound?.player_pick) {
    const selectedAsset = sortedAssetsWithPerformance.find(a => a.symbol === currentRound.player_pick);
    const selectedRanking = selectedAsset ? sortedAssetsWithPerformance.indexOf(selectedAsset) : -1;
    const isWinning = selectedRanking === 0;
    
    if (currentRound?.phase === 'RESULTS') {
      // Check if session is cashed out
      if (gameSession.currentSession?.session_status === 'cashed_out') {
        winLossStatus = 'cashed_out';
      } else {
        winLossStatus = isWinning ? 'won' : 'lost';
      }
    } else {
      winLossStatus = isWinning ? 'winning' : 'losing';
    }
  }

  const showConnectionModal = false; // Could be enabled if WebSocket disconnects

  return (
    <>
      <div className="betting-container">
      {showConnectionModal && (
        <div className="connection-modal-overlay">
          <div className="connection-modal">
            <div className="connection-modal-icon">🔌</div>
            <h2>Connection Lost</h2>
            <p>Unable to connect to betting server.</p>
            <p className="connection-modal-instruction">
              Please refresh the page to reconnect.
            </p>
            <button 
              onClick={() => window.location.reload()} 
              className="connection-modal-button"
            >
              🔄 Refresh Page
            </button>
            <div className="connection-modal-note">
              Betting is disabled until connection is restored
            </div>
          </div>
        </div>
      )}
      
      <div className="betting-main">
        <div className="race-table">
          <div className="race-list">
            <div className="race-card">
              {/* Activity Ticker - absolute overlay on top of ladder */}
              <div className="ticker-overlay-wrapper">
                <ResultsTicker results={gameSession.recentResults} />
              </div>
              
              <ProgressionLadder 
                payoutLadder={gameSession.payoutLadder}
                currentSession={gameSession.currentSession}
                currentRound={gameSession.currentRound}
                winLossStatus={winLossStatus}
                betAmount={betAmount}
                hasBoost={boostAppliesToCurrentSession}
                boostValue={boostValue}
              />
              
              <RaceTrack 
                race={currentRace}
                getAssetCSSColor={getAssetCSSColor}
                playerPick={currentRound?.player_pick}
                sortedAssetsWithPerformance={sortedAssetsWithPerformance}
                currentRound={currentRound}
                currentSession={gameSession.currentSession}
                isBettingEnabled={isBettingEnabled}
                bettingTimeRemaining={bettingTimeRemaining}
                racingTimeRemaining={racingTimeRemaining}
                betAmount={betAmount}
                isLoadingSession={isLoading}
                winLossStatus={winLossStatus}
                matchupHistory={gameSession.matchupHistory}
              />
            
            <div className="controls-wrapper">
              {/* Error notification - overlay toast over controls */}
              {error && (
                <div className="error-notification">
                  <span>{error}</span>
                  <button onClick={clearError}>×</button>
                </div>
              )}
              
              <GameSessionControls 
                hasBoost={boostAppliesToCurrentSession}
                boostValue={boostValue}
              />
            </div>
            </div>
          </div>
        </div>
      </div>
      
      {showRaceStartModal && (
        <div className="race-start-modal-overlay">
          <div className="race-start-modal">
            <img
              src="/assets/ui/racestarting.webp"
              alt="Race Starting"
              className="race-start-animation"
            />
          </div>
        </div>
      )}
      
      <BetNotificationContainer
        notifications={notifications}
        onDismiss={removeNotification}
      />
      
      {/* Share Modal - shown after Round 1 win */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        normalPayout={3.6}
        boostedPayout={Number((3.6 * boostValue).toFixed(1))}
        onBoostClaimed={refreshStatus}
      />
    </div>
    </>
  );
};

export default BettingContainer;
